import clsx from 'clsx';
import {
  type FocusEvent,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';

import { RadioGroupContext } from './RadioGroupContext.js';
import * as styles from './styles.sty.js';
import { useControllableState } from '../../core/hooks/useControllableState.js';
import { useFormControlState } from '../../core/hooks/useFormControlState.js';
import { DataTestId } from '../../core/types/data-props.js';
import type { FormControlProps } from '../../core/types/form-control-props.js';
import { MaskingProps } from '../../core/types/masking-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import { isDev } from '../../core/utils/is-node-environment.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { withFormFieldMessageContextProvider } from '../forms-core/components/form-field/FormFieldMessageContextProvider.js';
import { InternalBaseItem as BaseItem } from '../forms-core/components/form-field-messages/FormFieldMessageBaseItem.js';
import { useFormFieldMessagesContext } from '../forms-core/hooks/useFormFieldMessagesContext.js';
import { useSyncedFormControlProps } from '../forms-core/hooks/useSyncedFormControlProps.js';
import { FormFieldMessagesAction } from '../forms-core/state/formfield-messages-state-reducer.js';
import { useInputHint } from '../hooks/useInputHint.js';
import type { FormControlRef } from '../shared-types.js';

// #region Typings
/**
 * Properties for the RadioGroup component
 * @public
 */
export type RadioGroupProps = FormControlProps<
  string,
  (value: string, event?: FocusEvent<HTMLInputElement>) => void | never
> &
  StylingProps &
  WithChildren &
  DataTestId &
  MaskingProps;
// #endregion

const RadioGroupRoot = /* @__PURE__ */ forwardRef<
  FormControlRef,
  RadioGroupProps
>((props, forwardedRef) => {
  const {
    children,
    id: propId,
    disabled: propDisabled,
    value,
    name,
    defaultValue,
    controlState,
    onChange,
    readOnly: propReadOnly,
    required: propRequired,
    'aria-disabled': propAriaDisabled,
    'aria-describedby': ariaDescribedby,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  }: RadioGroupProps = props;

  const {
    disabled,
    required,
    id,
    ariaDisabled,
    readOnly: _readOnly,
  } = useSyncedFormControlProps({
    propDisabled,
    propRequired,
    propId,
    propAriaDisabled,
    propReadOnly,
  });
  const readOnly = !!_readOnly;

  const { state: messagesContextState, dispatch: messagesContextDispatch } =
    useFormFieldMessagesContext();
  const [controlStateHasError, hint] = useFormControlState(controlState);

  const hasError = controlState
    ? controlStateHasError
    : messagesContextState.showError;

  // _useInputHint will be replaced with new validation concept. APPDEV-8298
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const { inputProps, hintProps } = useInputHint({
    ariaDescribedby,
    hint,
    hasError,
  });

  const [controllableState, setControllableState] = useControllableState({
    defaultValue,
    value,
    onChange,
  });

  const wrapperRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(forwardedRef, (): FormControlRef<HTMLDivElement> => {
    return {
      element: wrapperRef.current,
      focus() {
        const checkedInput = wrapperRef.current?.querySelector(
          'input:checked',
        ) as HTMLInputElement;
        (
          checkedInput ??
          (wrapperRef.current?.querySelector('input') as HTMLInputElement)
        )?.focus();
      },
      validate() {
        if (controlState !== undefined) {
          return;
        }

        const inputs = wrapperRef.current?.querySelectorAll('input');
        inputs?.forEach((input) => {
          input?.checkValidity();
        });

        messagesContextDispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: true },
        });
      },
    };
  });

  if (isDev() && name === undefined) {
    // eslint-disable-next-line no-console
    console.warn(
      'RadioGroup: The "name" prop is missing, which is necessary to link the radio buttons correctly.',
    );
  }

  const radioGroupContextValue = useMemo(
    () => ({
      name,
      isDisabled: disabled,
      ariaDisabled,
      hasError,
      value: controllableState,
      onChange: setControllableState,
      readOnly,
      required,
      controlState,
    }),
    [
      name,
      disabled,
      ariaDisabled,
      hasError,
      controllableState,
      setControllableState,
      readOnly,
      required,
      controlState,
    ],
  );

  return (
    <div
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      aria-orientation="vertical"
      role="radiogroup"
      aria-live="polite"
      aria-disabled={propAriaDisabled}
      ref={wrapperRef}
      className={clsx(consumerClassName, styles.group, {
        [styles.readOnly]: readOnly,
        [styles.hasError]: hasError,
      })}
      style={consumerStyle}
      id={id}
      {...inputProps}
      {...remainingProps}
    >
      <RadioGroupContext.Provider value={radioGroupContextValue}>
        <Flex flexDirection="column" gap={8}>
          {children}
        </Flex>
      </RadioGroupContext.Provider>
      {hint && (
        <BaseItem
          variant={hasError ? 'error' : 'hint'}
          disabled={disabled || ariaDisabled}
          {...hintProps}
        >
          {hint}
        </BaseItem>
      )}
    </div>
  );
});

/**
 * Radio buttons allow users to select one option from
 * a group of related options. To group options, wrap the
 * `Radio` components inside a 'RadioGroup' component.
 * @public
 */
export const RadioGroup = withFormFieldMessageContextProvider<
  RadioGroupProps,
  FormControlRef
>(RadioGroupRoot);

(RadioGroup as typeof RadioGroup & { displayName: string }).displayName =
  'RadioGroup';
